import { PRODUCT_CATEGORIES_OPTIMIZED } from '../data/productCategoriesOptimized';

export const GARMENT_CATEGORY_ORDER = [
  'women-clothing',
  'men-clothing',
  'kids-boy',
  'kids-girl',
  'other',
  'unclassified',
];

const CATEGORY_LABELS = {
  'women-clothing': 'Women',
  'men-clothing': 'Men',
  'kids-boy': 'Kids (boy)',
  'kids-girl': 'Kids (girl)',
  other: 'Other',
  unclassified: 'No analysis yet',
};

export function formatGarmentCategoryLabel(value) {
  return CATEGORY_LABELS[value] || value || '—';
}

/** Stable bucket key from garment analysis metadata on a design. */
export function getDesignGarmentBucketKey(design) {
  const ga = design?.garmentAnalysis;
  const category = String(ga?.productCategory || '').trim();
  const type = String(ga?.productType || '').trim();

  if (!category || !type) return 'unclassified';

  if (type === 'other') {
    const other = String(ga?.otherTypeDescription || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 48);
    return other ? `${category}|other:${other}` : `${category}|other`;
  }

  return `${category}|${type}`;
}

function titleCaseSlug(slug = '') {
  return String(slug)
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** All product-type buckets from the garment catalog (Generate page types). */
export function buildGarmentTypeCatalogBuckets() {
  const buckets = [];

  for (const category of PRODUCT_CATEGORIES_OPTIMIZED) {
    const categoryValue = category.value;
    const categoryLabel = category.label || formatGarmentCategoryLabel(categoryValue);

    for (const type of category.types || []) {
      const typeValue = type.value;
      const typeLabel = type.label || titleCaseSlug(typeValue);
      buckets.push({
        key: `${categoryValue}|${typeValue}`,
        productCategory: categoryValue,
        productType: typeValue,
        categoryLabel,
        typeLabel,
        name: `${formatGarmentCategoryLabel(categoryValue)} · ${typeLabel}`,
        fromCatalog: true,
      });
    }
  }

  return buckets;
}

function createDynamicBucket(key, sampleAnalysis) {
  const [category = '', typePart = ''] = String(key).split('|');
  const categoryLabel = formatGarmentCategoryLabel(category);

  let typeLabel = titleCaseSlug(typePart);
  if (typePart === 'other' && sampleAnalysis?.otherTypeDescription) {
    typeLabel = `Other (${sampleAnalysis.otherTypeDescription})`;
  } else if (typePart.startsWith('other:')) {
    const desc = typePart.slice('other:'.length).replace(/-/g, ' ');
    typeLabel = desc ? `Other (${desc})` : 'Other';
  } else if (typePart === 'other') {
    typeLabel = 'Other';
  }

  return {
    key,
    productCategory: category,
    productType: typePart.includes(':') ? 'other' : typePart,
    categoryLabel,
    typeLabel,
    name: `${categoryLabel} · ${typeLabel}`,
    fromCatalog: false,
  };
}

/**
 * Virtual collections: catalog types + dynamic buckets for unlisted analysis + unclassified.
 */
export function buildVirtualGarmentCollections(
  designs = [],
  { includeEmptyTypes = false } = {}
) {
  const catalogBuckets = buildGarmentTypeCatalogBuckets();
  const bucketMap = new Map(catalogBuckets.map((b) => [b.key, { ...b, designs: [] }]));

  const ensureBucket = (key, sampleAnalysis) => {
    if (!bucketMap.has(key)) {
      bucketMap.set(key, { ...createDynamicBucket(key, sampleAnalysis), designs: [] });
    }
    return bucketMap.get(key);
  };

  for (const design of designs) {
    const key = getDesignGarmentBucketKey(design);
    const bucket = ensureBucket(key, design?.garmentAnalysis);
    bucket.designs.push(design);
  }

  if (!bucketMap.has('unclassified')) {
    bucketMap.set('unclassified', {
      key: 'unclassified',
      productCategory: 'unclassified',
      productType: '',
      categoryLabel: CATEGORY_LABELS.unclassified,
      typeLabel: '',
      name: CATEGORY_LABELS.unclassified,
      fromCatalog: false,
      designs: [],
    });
  }

  let collections = [...bucketMap.values()];

  if (!includeEmptyTypes) {
    collections = collections.filter((c) => (c.designs?.length || 0) > 0);
  }

  collections.sort((a, b) => {
    const catOrder =
      GARMENT_CATEGORY_ORDER.indexOf(a.productCategory) -
      GARMENT_CATEGORY_ORDER.indexOf(b.productCategory);
    if (catOrder !== 0) return catOrder;
    return String(a.name).localeCompare(String(b.name));
  });

  return collections;
}

export function groupVirtualCollectionsByCategory(collections = []) {
  const byCategory = new Map();

  for (const collection of collections) {
    const category = collection.productCategory || 'other';
    if (!byCategory.has(category)) byCategory.set(category, []);
    byCategory.get(category).push(collection);
  }

  return GARMENT_CATEGORY_ORDER.filter((key) => byCategory.has(key)).map((key) => ({
    key,
    label: formatGarmentCategoryLabel(key),
    collections: byCategory.get(key),
  }));
}

export function designOwnerLabel(design) {
  const user = design?.user;
  if (!user) return '';
  return user.name || user.email || user.phone || 'Unknown user';
}
